import { Game } from "@engine/Game";
import { PromoManager } from "@code/managers/PromoManager";
import { PopphinSaveSystem } from "@code/managers/PopphinSaveSystem";
import { CodexManager } from "@code/managers/CodexManager";
import { AchievementManager } from "@code/managers/AchievementManager";
import LandingScene from "@code/scenes/LandingScene.svelte";
import FirstLaunchScene from "@code/scenes/FirstLaunchScene.svelte";
import TitleScene from "@code/scenes/TitleScene.svelte";
import MinigameHubScene from "@code/scenes/MinigameHubScene.svelte";
import MatchGameScene from "@code/scenes/MatchGameScene.svelte";
import PauseScene from "@code/scenes/PauseScene.svelte";
import SettingsScene from "@code/scenes/SettingsScene.svelte";
import HealthContentLegalScene from "@code/scenes/HealthContentLegalScene.svelte";
import EthicalMonetizationScene from "@code/scenes/EthicalMonetizationScene.svelte";
import CodexScene from "@code/scenes/CodexScene.svelte";
import { STORAGE_KEYS } from "@config/constants/storage";
import creaturesData from "@config/data/creatures.json";
import habitatsData from "@config/data/habitats.json";
import type { MatchItem, MatchCategory } from "@gametype/match_learning/types";

const container = document.getElementById("game-container");
if (!container) throw new Error("game-container element missing");

const game = new Game({ container });

const promoManager = new PromoManager(game.promos);
const saveSystem = new PopphinSaveSystem(game.save);
const codexManager = new CodexManager(saveSystem);
const achievementManager = new AchievementManager(saveSystem);

const items: MatchItem[] = creaturesData.map((c) => ({
  id: c.id,
  displayName: c.displayName,
  categoryId: c.habitatId,
  factText: c.factText,
  factSourceId: c.factSourceId,
  spritePath: c.spritePath
}));

const categories: MatchCategory[] = habitatsData.map((h) => ({
  id: h.id,
  displayName: h.displayName,
  colorToken: h.colorToken,
  iconPath: ""
}));

async function safeLoad(name: string, fn: () => Promise<unknown>): Promise<void> {
  try {
    await fn();
  } catch (err) {
    console.warn(`[boot] optional load "${name}" failed:`, err);
  }
}

async function boot(): Promise<void> {
  game.boot();

  await Promise.all([
    safeLoad("locale:en", () => game.i18n.loadLocale("en", "/config/localisation/en.json")),
    safeLoad("promos", () => promoManager.revalidate())
  ]);

  const audioAssets: Array<[string, () => void]> = [
    ["music:title", () => game.audio.loadMusic("title", "/assets/audio/music/title_theme.mp3")],
    ["music:gameplay", () => game.audio.loadMusic("gameplay", "/assets/audio/music/match_gameplay.mp3")],
    ["sfx:correct", () => game.audio.loadSfx("correct", "/assets/audio/sfx/match_correct.mp3")],
    ["sfx:wrong", () => game.audio.loadSfx("wrong", "/assets/audio/sfx/match_wrong.mp3")],
    ["sfx:complete", () => game.audio.loadSfx("complete", "/assets/audio/sfx/round_complete.mp3")],
    ["sfx:click", () => game.audio.loadSfx("click", "/assets/audio/sfx/ui_click.mp3")]
  ];
  for (const [name, load] of audioAssets) {
    try {
      load();
    } catch (err) {
      console.warn(`[boot] audio "${name}" failed to register:`, err);
    }
  }

  await showLanding();
}

function mountSvelte<T extends Record<string, unknown>>(
  Component: new (opts: { target: HTMLElement; props: T }) => { $destroy(): void },
  props: T
): { unmount(): void } {
  container!.innerHTML = "";
  const instance = new Component({ target: container!, props });
  return { unmount: () => instance.$destroy() };
}

let currentUnmount: (() => void) | null = null;
let pauseResumeHandler: (() => void) | null = null;
let pauseRestartHandler: (() => void) | null = null;

function switchScene(unmount: () => void): void {
  currentUnmount?.();
  currentUnmount = unmount;
}

async function showLanding(): Promise<void> {
  const { unmount } = mountSvelte(LandingScene as never, {
    onPlay: () => void showFirstLaunchOrTitle(),
    onInstall: () => void game.install.prompt(),
    onDownload: () => void 0,
    installAvailable: game.install.isAvailable()
  });
  switchScene(unmount);
}

async function showFirstLaunchOrTitle(): Promise<void> {
  const acked = await game.save.read(STORAGE_KEYS.FIRST_LAUNCH_ACK);
  if (acked) return showTitle();
  const { unmount } = mountSvelte(FirstLaunchScene as never, {
    onAcknowledge: () => void showTitle()
  });
  switchScene(unmount);
}

async function showTitle(): Promise<void> {
  const state = await saveSystem.load();
  const promos = promoManager.current().promos;
  try {
    game.audio.playMusic("title");
  } catch (err) {
    console.warn("[audio] title music unavailable:", err);
  }
  const { unmount } = mountSvelte(TitleScene as never, {
    onContinue: () => void showHub(),
    onNewGame: () => void showHub(),
    onSettings: () => void showSettings(),
    onHealthContentLegal: () => void showHCL(),
    onEthicalMonetization: () => void showEM(),
    onCodex: () => void showCodex(),
    hasSave: state.totalSessions > 0,
    promos
  });
  switchScene(unmount);
}

async function showHub(): Promise<void> {
  const { unmount } = mountSvelte(MinigameHubScene as never, {
    onPickMinigame: () => void showGame(),
    onBack: () => void showTitle()
  });
  switchScene(unmount);
}

async function showGame(): Promise<void> {
  try {
    game.audio.playMusic("gameplay");
  } catch (err) {
    console.warn("[audio] gameplay music unavailable:", err);
  }
  const { unmount } = mountSvelte(MatchGameScene as never, {
    items,
    categories,
    onComplete: async (result: {
      correctCount: number;
      wrongCount: number;
      finalScore: number;
      bestStreak: number;
      itemsMatched: string[];
      sessionState?: unknown;
    }) => {
      const unlockedAch = await achievementManager.checkOnSessionComplete(
        {
          phase: "complete",
          currentRoundIdx: 0,
          currentItemIdx: 0,
          answers: [],
          score: result.finalScore,
          streak: 0,
          bestStreak: result.bestStreak,
          correctCount: result.correctCount,
          wrongCount: result.wrongCount
        },
        {
          totalItems: result.correctCount + result.wrongCount,
          correctCount: result.correctCount,
          wrongCount: result.wrongCount,
          bestStreak: result.bestStreak,
          finalScore: result.finalScore,
          itemsMatched: result.itemsMatched
        }
      );
      await saveSystem.recordSession({
        score: result.finalScore,
        matchedCreatureIds: result.itemsMatched,
        unlockedAchievementIds: unlockedAch
      });
      await showTitle();
    },
    onPause: () => void showPause({ resume: () => void 0 })
  });
  switchScene(unmount);
  pauseResumeHandler = null;
  pauseRestartHandler = null;
}

async function showPause(hooks: { resume: () => void }): Promise<void> {
  const previousUnmount = currentUnmount;
  const { unmount } = mountSvelte(PauseScene as never, {
    onResume: () => {
      currentUnmount = previousUnmount;
      hooks.resume();
    },
    onRestart: () => void showGame(),
    onSettings: () => void showSettings(),
    onQuit: () => void showTitle()
  });
  currentUnmount = () => {
    unmount.unmount ? unmount.unmount() : (unmount as unknown as () => void)();
  };
}

async function showSettings(): Promise<void> {
  const { unmount } = mountSvelte(SettingsScene as never, {
    onBack: () => void showTitle()
  });
  switchScene(unmount);
}

async function showHCL(): Promise<void> {
  const { unmount } = mountSvelte(HealthContentLegalScene as never, {
    onBack: () => void showTitle()
  });
  switchScene(unmount);
}

async function showEM(): Promise<void> {
  const commitmentLive = promoManager.current().commitment;
  const { unmount } = mountSvelte(EthicalMonetizationScene as never, {
    onBack: () => void showTitle(),
    commitmentLive
  });
  switchScene(unmount);
}

async function showCodex(): Promise<void> {
  const unlocked = await codexManager.unlockedIds();
  const { unmount } = mountSvelte(CodexScene as never, {
    unlockedIds: unlocked,
    onBack: () => void showTitle()
  });
  switchScene(unmount);
}

void boot().catch((err) => {
  console.error("[boot] fatal:", err);
  if (container) {
    container.innerHTML = `<div role="alert" style="padding:2rem;font-family:sans-serif;text-align:center;">
      <h1>Something went wrong loading the game.</h1>
      <p>Please refresh the page. If it keeps happening, contact hello@popphin.com.</p>
    </div>`;
  }
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    currentUnmount?.();
    game.shutdown();
  });
}

// Silence unused var lint until pause-resume is fully implemented
void pauseResumeHandler;
void pauseRestartHandler;
